import { PettyWorkStatus } from "features/PettyWorks/constants/petty-work.constants";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import type { PettyWork } from "features/PettyWorks/types/petty-work.types";

const PW_CANCELLABLE_STATUSES: ReadonlySet<string> = new Set([
  PettyWorkStatus.NEW,
  PettyWorkStatus.IN_PROGRESS,
  PettyWorkStatus.ON_HOLD,
]);

export type PettyWorkLifecycleVisibility = {
  start: boolean;
  hold: boolean;
  resume: boolean;
  /** IN_PROGRESS → PENDING_COMPLETION_APPROVAL (replaces old single-step complete) */
  submitCompletion: boolean;
  /** PENDING_COMPLETION_APPROVAL → READY_FOR_BILLING */
  approveForBilling: boolean;
  /** PENDING_COMPLETION_APPROVAL → IN_PROGRESS */
  rejectCompletion: boolean;
  /** READY_FOR_BILLING → COMPLETED */
  submitBilling: boolean;
  cancel: boolean;
};

/** Which lifecycle actions are allowed for this record (permissions + status). */
export function getPettyWorkLifecycleVisibility(
  pettyWork: Pick<PettyWork, "status">,
  can: (permission: string) => boolean,
): PettyWorkLifecycleVisibility {
  const raw = String(pettyWork.status ?? "").trim();
  return {
    start: can(Permissions.PETTY_WORK.START) && raw === PettyWorkStatus.NEW,
    hold: can(Permissions.PETTY_WORK.HOLD) && raw === PettyWorkStatus.IN_PROGRESS,
    resume: can(Permissions.PETTY_WORK.RESUME) && raw === PettyWorkStatus.ON_HOLD,
    submitCompletion:
      can(Permissions.PETTY_WORK.COMPLETE) && raw === PettyWorkStatus.IN_PROGRESS,
    approveForBilling:
      can(Permissions.PETTY_WORK.APPROVE) &&
      raw === PettyWorkStatus.PENDING_COMPLETION_APPROVAL,
    rejectCompletion:
      can(Permissions.PETTY_WORK.REJECT) &&
      raw === PettyWorkStatus.PENDING_COMPLETION_APPROVAL,
    submitBilling:
      can(Permissions.PETTY_WORK.SUBMIT_BILLING) &&
      raw === PettyWorkStatus.READY_FOR_BILLING,
    cancel: can(Permissions.PETTY_WORK.CANCEL) && PW_CANCELLABLE_STATUSES.has(raw),
  };
}

export function pettyWorkLifecycleAnyVisible(
  v: PettyWorkLifecycleVisibility,
): boolean {
  return (
    v.start ||
    v.hold ||
    v.resume ||
    v.submitCompletion ||
    v.approveForBilling ||
    v.rejectCompletion ||
    v.submitBilling ||
    v.cancel
  );
}
